if (!customElements.get('category-widget')) {
	class CategoryWidget extends HTMLElement {
		constructor() {
			super();

			this.splide = null;
			this.isInitialized = false;
			this.settings = {};
			this.resizeTimeout = null;
			this.onResize = this.onResize.bind(this);
		}

		connectedCallback() {
			this.waitForSplide().then(() => {
				this.init();
			});
		}

		waitForSplide() {
			return new Promise((resolve) => {
				const checkSplide = () => {
					if (window.Splide) {
						resolve();
					} else {
						setTimeout(checkSplide, 50);
					}
				};
				checkSplide();
			});
		}

		disconnectedCallback() {
			this.destroy();
		}

		init() {
			if (this.isInitialized || !window.Splide) return;

			try {
				this.settings = this.getSettings();
				this.initializeSplide();
				this.setupEventListeners();
				this.isInitialized = true;
			} catch (error) {
				console.error('CategoryWidget initialization failed:', error);
			}
		}

		getSettings() {
			const settingsAttr = this.getAttribute('data-widget-settings');
			const defaultSettings = {
				itemsPerView: 3,
				showArrows: true,
				showCollectionTitle: true,
				style: 'rounded',
				containerWidth: 100,
				autoPlay: false,
			};

			try {
				return settingsAttr ? { ...defaultSettings, ...JSON.parse(settingsAttr) } : defaultSettings;
			} catch (error) {
				console.warn('CategoryWidget: invalid settings, using defaults', error);
				return defaultSettings;
			}
		}

		initializeSplide() {
			const mobileWidth = parseInt(this.dataset.mobileWidth || '150') || 150;
			const desktopWidth = parseInt(this.dataset.desktopWidth || '200') || 200;
			const gapFromSettings = this.settings.gap || 16;
			const gapFromData = this.dataset.gap || '16';
			const gap = gapFromData ? `${gapFromData}px` : `${gapFromSettings}px`;
			const screenWidth = window.innerWidth;
			const currentItemWidth = screenWidth >= 768 ? desktopWidth : mobileWidth;

			const splideOptions = {
				type: 'slide',
				gap: gap,
				pagination: false,
				arrows: false,
				fixedWidth: currentItemWidth + 'px',
				perMove: 1,
				focus: 0,
				trimSpace: 'move',
			};

			this.splide = new window.Splide(this, splideOptions);

			this.splide.on('mounted', () => {
				this.updateCustomStyling();
			});

			this.splide.on('ready', () => {
				const parentBlock = this.closest('.collection-category-widget-block');
				if (parentBlock) {
					const prevBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--prev');
					const nextBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--next');
					this.updateHeaderButtonStates(prevBtn, nextBtn);
				}
			});

			this.splide.on('moved', () => {
				const parentBlock = this.closest('.collection-category-widget-block');
				if (parentBlock) {
					const prevBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--prev');
					const nextBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--next');
					this.updateHeaderButtonStates(prevBtn, nextBtn);
				}
			});

			this.splide.mount();
		}

		updateCustomStyling() {
			const prevArrow = this.querySelector('.category-widget__nav--prev');
			const nextArrow = this.querySelector('.category-widget__nav--next');

			if (prevArrow) {
				prevArrow.innerHTML = '‹';
				prevArrow.setAttribute('type', 'button');
				prevArrow.setAttribute('aria-label', 'Previous');
			}

			if (nextArrow) {
				nextArrow.innerHTML = '›';
				nextArrow.setAttribute('type', 'button');
				nextArrow.setAttribute('aria-label', 'Next');
			}
		}

		getItemsPerView() {
			const screenWidth = window.innerWidth;
			const mobileWidth = parseInt(this.dataset.mobileWidth || '150') || 150;
			const desktopWidth = parseInt(this.dataset.desktopWidth || '200') || 200;
			const currentItemWidth = screenWidth >= 768 ? desktopWidth : mobileWidth;
			const containerWidth = screenWidth * 0.9;
			const gap = parseInt(this.dataset.gap || '16') || 16;

			const itemsPerView = Math.floor((containerWidth + gap) / (currentItemWidth + gap));

			if (screenWidth >= 1400) {
				return Math.min(Math.max(itemsPerView, 6), 8);
			} else if (screenWidth >= 1200) {
				return Math.min(Math.max(itemsPerView, 5), 7);
			} else if (screenWidth >= 992) {
				return Math.min(Math.max(itemsPerView, 4), 6);
			} else if (screenWidth >= 768) {
				return Math.min(Math.max(itemsPerView, 3), 5);
			} else if (screenWidth >= 576) {
				return Math.min(Math.max(itemsPerView, 2), 4);
			} else {
				return Math.min(Math.max(itemsPerView, 1), 3);
			}
		}

		getItemsPerViewForWidth(screenWidth) {
			const mobileWidth = parseInt(this.dataset.mobileWidth || '150') || 150;
			const desktopWidth = parseInt(this.dataset.desktopWidth || '200') || 200;

			const currentItemWidth = screenWidth >= 768 ? desktopWidth : mobileWidth;
			const containerWidth = screenWidth * 0.9;
			const gap = parseInt(this.dataset.gap || '16') || 16;

			const itemsPerView = Math.floor((containerWidth + gap) / (currentItemWidth + gap));

			if (screenWidth >= 1400) {
				return Math.min(Math.max(itemsPerView, 6), 8);
			} else if (screenWidth >= 1200) {
				return Math.min(Math.max(itemsPerView, 5), 7);
			} else if (screenWidth >= 992) {
				return Math.min(Math.max(itemsPerView, 4), 6);
			} else if (screenWidth >= 768) {
				return Math.min(Math.max(itemsPerView, 3), 5);
			} else if (screenWidth >= 576) {
				return Math.min(Math.max(itemsPerView, 2), 4);
			} else {
				return Math.min(Math.max(itemsPerView, 1), 3);
			}
		}

		getGapSize() {
			if (this.dataset.gap) {
				return `${this.dataset.gap}px`;
			}

			const screenWidth = window.innerWidth;
			if (screenWidth >= 1024) return '2rem';
			if (screenWidth >= 768) return '1.5rem';
			return '1rem';
		}

		setupEventListeners() {
			window.addEventListener('resize', this.onResize, { passive: true });

			const images = this.querySelectorAll('img');
			images.forEach((img) => {
				img.addEventListener('dragstart', (e) => e.preventDefault());
			});

			this.setupHeaderNavigation();
		}

		setupHeaderNavigation() {
			const parentBlock = this.closest('.collection-category-widget-block');
			if (!parentBlock) return;

			const prevBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--prev');
			const nextBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--next');

			if (prevBtn) {
				prevBtn.addEventListener('click', () => {
					if (this.splide) {
						this.splide.go('-1');
					}
				});
			}

			if (nextBtn) {
				nextBtn.addEventListener('click', () => {
					if (this.splide) {
						this.splide.go('+1');
					}
				});
			}
		}

		updateHeaderButtonStates(prevBtn, nextBtn) {
			if (!this.splide || !prevBtn || !nextBtn) return;

			const currentIndex = this.splide.index;
			const totalSlides = this.splide.length;
			const perPage = this.splide.options.perPage;
			const isLoop = this.splide.options.type === 'loop';

			const arrowsNeeded = this.areArrowsNeeded();

			const navContainer = prevBtn.closest('.collection-category-widget-block__nav');
			if (navContainer) {
				navContainer.style.display = arrowsNeeded ? 'flex' : 'none';
			}

			if (!arrowsNeeded) return;

			if (!isLoop) {
				const prevDisabled = currentIndex <= 0;
				prevBtn.disabled = prevDisabled;

				if (this.splide.options.fixedWidth) {
					const track = this.querySelector('.splide__track');
					const list = this.querySelector('.splide__list');

					if (track && list) {
						const trackWidth = track.offsetWidth;
						const listWidth = list.scrollWidth;
						const currentTransform = list.style.transform;

						const translateMatch = currentTransform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
						const currentTranslate = translateMatch ? Math.abs(parseFloat(translateMatch[1])) : 0;

						const scrolledDistance = currentTranslate + trackWidth;
						const isAtEnd = scrolledDistance >= listWidth * 0.95;
						nextBtn.disabled = isAtEnd;
					} else {
						const nextDisabled = currentIndex >= totalSlides - 1;
						nextBtn.disabled = nextDisabled;
					}
				} else {
					const maxIndex = Math.max(0, totalSlides - perPage);
					nextBtn.disabled = currentIndex >= maxIndex;
				}
			} else {
				prevBtn.disabled = false;
				nextBtn.disabled = false;
			}
		}

		areArrowsNeeded() {
			if (!this.splide) return false;

			const totalSlides = this.splide.length;

			if (this.splide.options.fixedWidth) {
				const containerWidth = this.offsetWidth;
				const slides = this.querySelectorAll('.splide__slide');
				let totalContentWidth = 0;

				slides.forEach((slide, index) => {
					totalContentWidth += slide.getBoundingClientRect().width;
					if (index < slides.length - 1) {
						const gapValue = this.splide.options.gap;
						const gap = typeof gapValue === 'string' ? parseInt(gapValue) : gapValue || 0;
						totalContentWidth += gap;
					}
				});

				return totalContentWidth > containerWidth;
			}
			const perPage = this.splide.options.perPage;

			if (this.splide.options.autoWidth) {
				const containerWidth = this.offsetWidth;
				const slides = this.querySelectorAll('.splide__slide');
				let totalContentWidth = 0;

				slides.forEach((slide, index) => {
					totalContentWidth += slide.getBoundingClientRect().width;
					if (index < slides.length - 1) {
						const gapValue = this.splide.options.gap;
						const gap = typeof gapValue === 'string' ? parseInt(gapValue) : gapValue || 0;
						totalContentWidth += gap;
					}
				});

				return totalContentWidth > containerWidth;
			}

			if (perPage === 'auto') {
				const containerWidth = this.offsetWidth;
				const slides = this.querySelectorAll('.splide__slide');
				let totalContentWidth = 0;

				slides.forEach((slide, index) => {
					totalContentWidth += slide.getBoundingClientRect().width;
					if (index < slides.length - 1) {
						const gapValue = this.splide.options.gap;
						const gap = typeof gapValue === 'string' ? parseInt(gapValue) : gapValue || 0;
						totalContentWidth += gap;
					}
				});

				return totalContentWidth > containerWidth;
			}

			return totalSlides > (perPage || 1);
		}

		onResize() {
			if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				if (this.splide) {
					const itemWidthFromSettings = this.settings.itemWidth || 0;
					const itemWidthFromData = this.dataset.itemWidth || '0';
					const useFixedWidth = itemWidthFromSettings > 0 || parseInt(itemWidthFromData) > 0;

					if (!useFixedWidth) {
						const newPerPage = this.getItemsPerView();
						const newGap = this.getGapSize();

						this.splide.options = {
							...this.splide.options,
							perPage: newPerPage,
							gap: newGap,
						};

						this.splide.refresh();
					} else {
						this.splide.refresh();
					}

					this.updateArrowVisibilityAfterResize();
				}
			}, 150);
		}

		updateArrowVisibilityAfterResize() {
			const parentBlock = this.closest('.collection-category-widget-block');
			if (!parentBlock) return;

			const prevBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--prev');
			const nextBtn = parentBlock.querySelector('.collection-category-widget-block__nav-btn--next');

			if (prevBtn && nextBtn) {
				this.updateHeaderButtonStates(prevBtn, nextBtn);
			}
		}

		go(index) {
			if (this.splide) {
				this.splide.go(index);
			}
		}

		next() {
			if (this.splide) {
				this.splide.go('+1');
			}
		}

		prev() {
			if (this.splide) {
				this.splide.go('-1');
			}
		}

		getCurrentIndex() {
			return this.splide ? this.splide.index : 0;
		}

		getTotalSlides() {
			return this.splide ? this.splide.length : 0;
		}

		destroy() {
			if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

			if (this.splide) {
				this.splide.destroy();
				this.splide = null;
			}

			window.removeEventListener('resize', this.onResize);
			this.isInitialized = false;
		}
	}

	customElements.define('category-widget', CategoryWidget);
}
